import { useContext, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { digObject, toggleArray } from 'functions/utilities'
import multiFilteredObjectArraySelector from 'functions/multiFilteredObjectArraySelector'

import useLatestEntity from 'agency/hooks/useLatestEntity'
import useThunkDispatch from 'hooks/useThunkDispatch'
import useWatchEntityUpdates from 'hooks/useWatchEntityUpdates'

import * as agencyActions from 'agency/redux/modules/agency'

// IMPORTANT - Make sure you have added a PageContext.Provider
// to the page that contains this hook

import PageContext from 'contexts/pageContext'

const createImageDownloadPreset = (preset, agency, dispatch, requestOptions) => {
  const {
    key, title, width, height,
  } = preset

  const customPresets = digObject(agency, 'options.image_download_presets', [])
  const updatedPresets = JSON.parse(JSON.stringify(customPresets))

  const updatedPreset = {
    key,
    subtitle: `Custom (${width}x${height})`,
    title,
    type: `custom_${width}x${height}`,
  }

  updatedPresets.push(updatedPreset)

  return updateAgencyOption(agency, 'image_download_presets', updatedPresets, dispatch, requestOptions).then(
    response => ({
      ...response,
      updatedPreset,
    }),
  )
}

const deleteImageDownloadPreset = (preset, agency, dispatch, requestOptions) => {
  const { type } = preset

  const customPresets = digObject(agency, 'options.image_download_presets', [])
  const updatedPresets = JSON.parse(JSON.stringify(customPresets))

  const index = updatedPresets.findIndex(p => p.type === type)

  if (index !== -1){
    updatedPresets.splice(index, 1)
  }

  return updateAgencyOption(agency, 'image_download_presets', updatedPresets, dispatch, requestOptions)
}

const editTaskPricing = (taskType, provider, showEditAgencyTaskPricingModal) => new Promise((resolve, reject) => {
  if (showEditAgencyTaskPricingModal){
    const payload = {
      provider,
      taskType,
    }

    showEditAgencyTaskPricingModal(payload)

    return resolve({ success: true, result: payload })
  }

  return reject(new Error('showEditAgencyTaskPricingModal not defined in PageContext callbacks'))
})

const toggleHiddenPortalOptionIds = (agency, id, hiddenOptionValues, dispatch, portal) => {
  const updatedHiddenPortalOptionIds = toggleArray(hiddenOptionValues, id)

  updateAgencyOption(agency, 'disabled_portal_option_values', {
    [portal.short_code]: updatedHiddenPortalOptionIds,
  }, dispatch)
}

const updateAgencyOption = (agency, key, value, dispatch, requestOptions) => {
  const agencyOptions = agency.options || {}

  const updatedAgency = {
    id: agency.id,
    options: {
      ...JSON.parse(JSON.stringify(agencyOptions)),
      [key]: value,
    },
  }

  return updateAgency(agency, updatedAgency, dispatch, requestOptions)
}

const updateAgency = (agency, params, dispatch, requestOptions) => {
  const { updateAgency: updateFn } = agencyActions

  const { data, options } = params

  const updatedParams = {
    ...params,
  }

  // Prevent overwriting agency data or options accidentally
  // in case all options are not passed back in params object
  if (data){
    const agencyData = agency.data || {}
    updatedParams.data = JSON.stringify({
      ...JSON.parse(JSON.stringify(agencyData)),
      ...data,
    })
  }

  if (options){
    const agencyOptions = agency.options || {}
    updatedParams.options = JSON.stringify({
      ...JSON.parse(JSON.stringify(agencyOptions)),
      ...options,
    })
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

export function useRelations(agency){
  const { brand_id } = agency

  const entities = useSelector(reduxState => reduxState.entities)

  const { brands } = entities

  const brand = brands[brand_id] || {}

  return {
    brand,
    entities,
  }
}

function useAgency(initAgency){
  const { entity: agency } = useLatestEntity(initAgency, 'agencies')
  const {
    updatedEntities: { agencies: agenciesUpdatedAt },
  } = useWatchEntityUpdates(['agencies'])

  const dispatch = useThunkDispatch()

  const { loading, updating } = useSelector(reduxState => reduxState.agencies)

  const relations = useRelations(agency)
  const { brand, entities } = relations

  const { callbacks } = useContext(PageContext)
  const { showEditAgencyTaskPricingModal } = callbacks || {}

  const agencyOptions = agency.options || {}

  const watermarkImage = useMemo(() => {
    const watermarkImageFilters = [
      { key: 'attachable_type', value: 'Agency' },
      { key: 'attachable_id', value: agency.id },
      { key: 'attachment_type', value: 'watermark_image' },
    ]

    return multiFilteredObjectArraySelector({ entities }, 'attachments', watermarkImageFilters)[0] || {}
  }, [agenciesUpdatedAt, agency.id])

  return {
    agency,
    agencyOptions,
    brand,
    callbacks: {
      editTaskPricing: (taskType, provider) => editTaskPricing(taskType, provider, showEditAgencyTaskPricingModal),
      createImageDownloadPreset: (preset, requestOptions) => createImageDownloadPreset(preset, agency, dispatch, requestOptions),
      deleteImageDownloadPreset: (preset, requestOptions) => deleteImageDownloadPreset(preset, agency, dispatch, requestOptions),
      updateAgency: (updatedParams, requestOptions) => updateAgency(agency, updatedParams, dispatch, requestOptions),
      updateAgencyOption: (key, value, requestOptions) => updateAgencyOption(agency, key, value, dispatch, requestOptions),
      updateAgencyPortalOptions: (id, hiddenOptionValues, portal) => toggleHiddenPortalOptionIds(agency, id, hiddenOptionValues, dispatch, portal),
    },
    loading,
    updating,
    watermarkImage,
  }
}

export default useAgency
